import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createVocalAudioChain,
  disposeMonoAudioChain,
  isSignalPresent,
  measureVolume,
  MONO_SILENCE_FRAMES,
  smoothPitchHistory,
  updateNoiseBaseline,
  type MonoAudioChainNodes,
} from '@/play/lib/monoPitchDetectorCore';
import {
  freqToMidi,
  resolveSingOctave,
} from '../lib/melody/clingCantaPitch';
import type { SingPitchId } from '../lib/melody/clingCantaConfig';
import { SING_PITCH_BY_ID } from '../lib/melody/clingCantaConfig';
import {
  getVoiceProfile,
  voiceToDetectProfile,
  type VoiceTessitura,
  type VoiceTessituraProfile,
} from '../lib/melody/clingVoiceConfig';
import {
  gameMidiFromDetected,
  smoothStableGameMidi,
  stablePitchId,
  type PitchIdHoldState,
} from '../lib/melody/juniorPitchStabilizer';

export interface JuniorPitchState {
  frequency: number | null;
  /** MIDI calibrado para el juego (con transposición de tesitura). */
  midi: number | null;
  pitchId: SingPitchId | null;
  volume: number;
  micActive: boolean;
  isSinging: boolean;
  micReady: boolean;
  micError: string | null;
  simulationMode: boolean;
}

const INITIAL: JuniorPitchState = {
  frequency: null,
  midi: null,
  pitchId: null,
  volume: 0,
  micActive: false,
  isSinging: false,
  micReady: false,
  micError: null,
  simulationMode: false,
};

const MISS_FRAMES_TO_CLEAR = 10;
const MIC_ACTIVE_THRESHOLD = 0.003;

export function useJuniorPitchDetection(
  allowedLanes: SingPitchId[],
  voiceTessitura: VoiceTessitura
) {
  const [state, setState] = useState<JuniorPitchState>(INITIAL);
  const allowedRef = useRef(allowedLanes);
  allowedRef.current = allowedLanes;

  const voiceProfileRef = useRef(getVoiceProfile(voiceTessitura));
  voiceProfileRef.current = getVoiceProfile(voiceTessitura);

  const chainRef = useRef<MonoAudioChainNodes | null>(null);
  const rafRef = useRef<number | null>(null);
  const enabledRef = useRef(false);
  const simPitchRef = useRef<SingPitchId | null>(null);
  const simulationRef = useRef(false);

  const freqHistoryRef = useRef<number[]>([]);
  const silenceFramesRef = useRef(0);
  const missFramesRef = useRef(0);
  const prevPeakRmsRef = useRef(0);
  const noiseBaselineRef = useRef(0);
  const midiSmoothRef = useRef<number | null>(null);
  const pitchHoldRef = useRef<PitchIdHoldState>({
    locked: null,
    candidate: null,
    candidateFrames: 0,
  });

  const resetPitchTracking = useCallback(() => {
    freqHistoryRef.current = [];
    midiSmoothRef.current = null;
    missFramesRef.current = 0;
    pitchHoldRef.current = { locked: null, candidate: null, candidateFrames: 0 };
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const clearPitch = useCallback(
    (volume: number) => {
      resetPitchTracking();
      setState((prev) => ({
        ...prev,
        frequency: null,
        midi: null,
        pitchId: null,
        volume,
        micActive: volume >= MIC_ACTIVE_THRESHOLD,
        isSinging: false,
      }));
    },
    [resetPitchTracking]
  );

  const publishVolume = useCallback((volume: number) => {
    setState((prev) => {
      if (prev.volume === volume && prev.micActive === volume >= MIC_ACTIVE_THRESHOLD) return prev;
      return {
        ...prev,
        volume,
        micActive: volume >= MIC_ACTIVE_THRESHOLD,
      };
    });
  }, []);

  const teardown = useCallback(() => {
    stopLoop();
    enabledRef.current = false;
    disposeMonoAudioChain(chainRef.current);
    chainRef.current = null;
    silenceFramesRef.current = 0;
    prevPeakRmsRef.current = 0;
    noiseBaselineRef.current = 0;
    resetPitchTracking();
  }, [stopLoop, resetPitchTracking]);

  const openMicChain = useCallback(async (): Promise<boolean> => {
    disposeMonoAudioChain(chainRef.current);
    chainRef.current = null;
    silenceFramesRef.current = 0;
    prevPeakRmsRef.current = 0;
    noiseBaselineRef.current = 0;
    resetPitchTracking();

    try {
      const detectProfile = voiceToDetectProfile(voiceProfileRef.current);
      const chain = await createVocalAudioChain(detectProfile);
      chainRef.current = chain;
      simulationRef.current = false;
      return true;
    } catch {
      simulationRef.current = true;
      return false;
    }
  }, [resetPitchTracking]);

  const freqHistoryLen = (id: VoiceTessituraProfile['id']) => (id === 'grave' ? 8 : 5);

  const tick = useCallback(() => {
    if (!enabledRef.current) return;

    const voiceProfile = voiceProfileRef.current;

    if (simulationRef.current) {
      const sim = simPitchRef.current;
      if (sim) {
        const midi = SING_PITCH_BY_ID[sim]?.midi ?? null;
        setState((prev) => ({
          ...prev,
          pitchId: sim,
          isSinging: true,
          volume: 0.5,
          midi,
          frequency: null,
          micReady: true,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          pitchId: null,
          isSinging: false,
          volume: 0,
          midi: null,
          frequency: null,
        }));
      }
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const chain = chainRef.current;
    if (!chain) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    chain.analyser.getFloatTimeDomainData(chain.buffer);
    const { rms, peakRms, volume } = measureVolume(chain.buffer);
    const noiseBaseline = noiseBaselineRef.current;
    const micActive = volume >= MIC_ACTIVE_THRESHOLD;

    if (!isSignalPresent(volume, peakRms, prevPeakRmsRef.current, noiseBaseline)) {
      noiseBaselineRef.current = updateNoiseBaseline(noiseBaseline, rms, volume);
      prevPeakRmsRef.current = peakRms;
      silenceFramesRef.current += 1;
      if (silenceFramesRef.current > MONO_SILENCE_FRAMES) {
        clearPitch(volume);
      } else {
        publishVolume(volume);
      }
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    prevPeakRmsRef.current = peakRms;
    noiseBaselineRef.current = updateNoiseBaseline(noiseBaseline, rms, volume);
    silenceFramesRef.current = 0;

    const raw = chain.detectPitch(chain.buffer);
    if (!raw) {
      missFramesRef.current += 1;
      if (missFramesRef.current >= MISS_FRAMES_TO_CLEAR) {
        clearPitch(volume);
      } else {
        setState((prev) => ({
          ...prev,
          volume,
          micActive,
          isSinging: prev.midi != null,
        }));
      }
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const { smoothed } = smoothPitchHistory(
      freqHistoryRef.current,
      raw,
      freqHistoryLen(voiceProfile.id)
    );

    missFramesRef.current = 0;
    const resolvedHz = resolveSingOctave(smoothed);
    const gameMidiRaw = gameMidiFromDetected(
      freqToMidi(resolvedHz),
      midiSmoothRef.current
    );
    const smoothedMidi = smoothStableGameMidi(midiSmoothRef.current, gameMidiRaw, voiceProfile);
    midiSmoothRef.current = smoothedMidi;

    const stable = stablePitchId(
      smoothedMidi,
      allowedRef.current,
      voiceProfile,
      pitchHoldRef.current
    );
    pitchHoldRef.current = stable.hold;
    const pitchId = stable.pitchId;
    const midi = smoothedMidi;

    setState({
      frequency: resolvedHz,
      midi,
      pitchId,
      volume,
      micActive: true,
      isSinging: true,
      micReady: true,
      micError: null,
      simulationMode: false,
    });

    rafRef.current = requestAnimationFrame(tick);
  }, [clearPitch, publishVolume]);

  const startDetection = useCallback(() => {
    enabledRef.current = true;
    stopLoop();
    const chain = chainRef.current;
    if (chain) {
      void chain.context.resume().catch(() => {});
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [stopLoop, tick]);

  const stopDetection = useCallback(() => {
    enabledRef.current = false;
    stopLoop();
    clearPitch(0);
  }, [stopLoop, clearPitch]);

  const requestMic = useCallback(async (): Promise<boolean> => {
    teardown();
    const ok = await openMicChain();
    if (ok) {
      setState({
        ...INITIAL,
        micReady: true,
        simulationMode: false,
      });
      startDetection();
      return true;
    }
    setState({
      ...INITIAL,
      micReady: true,
      simulationMode: true,
      micError: 'Sin micrófono — usa ↑ ↓ y espacio para cantar',
    });
    startDetection();
    return false;
  }, [teardown, openMicChain, startDetection]);

  const simulatePitch = useCallback((pitchId: SingPitchId | null) => {
    simPitchRef.current = pitchId;
  }, []);

  useEffect(() => {
    resetPitchTracking();
    if (!chainRef.current || simulationRef.current) return;
    void (async () => {
      const wasEnabled = enabledRef.current;
      const ok = await openMicChain();
      if (!ok) {
        simulationRef.current = true;
        setState((prev) => ({
          ...prev,
          simulationMode: true,
          micError: 'Sin micrófono — usa ↑ ↓ y espacio para cantar',
        }));
      }
      if (wasEnabled) startDetection();
    })();
  }, [voiceTessitura, openMicChain, startDetection, resetPitchTracking]);

  useEffect(() => () => teardown(), [teardown]);

  return {
    ...state,
    requestMic,
    startDetection,
    stopDetection,
    simulatePitch,
  };
}
